import React, { useRef, useState } from 'react';
import { Menu, Icon, SemanticICONS } from 'semantic-ui-react';
import CloseablePopup from './closeable_popup';

export interface PopupDropdownProps {
  trigger?: TypeMePlease;
  content?: TypeMePlease;
  children?: TypeMePlease | TypeMePlease[];
  basic?: boolean;
  position?: string;
  offset?: string | number[];
  className?: string;
  selection?: boolean;
  search?: boolean;
  value?: string;
  onChange?: AnyFunc;
  placeholder?: string;
  options?: TypeMePlease;
  clearable?: boolean;
  loading?: boolean;
}

export interface PopupDropdownMenuProps {
  children?: TypeMePlease | TypeMePlease[];
  className?: string;
  vertical?: boolean;
}

export interface PopupDropdownItemProps {
  icon?: string;
  children?: any;
  text?: string;
  onClick?: AnyFunc;
  active?: boolean;
  selected?: boolean;
}

const { Provider, Consumer } = React.createContext<TypeMePlease>(undefined);

const PopupDropdown = (props: PopupDropdownProps) => {
  const [popupOpened, setPopupOpened] = useState(false);
  const popup = useRef<TypeMePlease>();
  const searchInput = useRef<TypeMePlease>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [activeOptionIndex, setActiveOptionIndex] = useState<number>();

  const popupOnOpen = () => {
    setPopupOpened(true);
  };
  const popupOnClose = () => {
    if (searchInput.current) {
      searchInput.current.blur();
    }
    setSearchTerm(null);
    setPopupOpened(false);
  };
  const closePopup = () => {
    popup.current.close();
    popupOnClose();
  };

  const selectValue = (option) => {
    setSearchTerm(null);
    props.onChange(option);
    closePopup();
  };

  const filteredOptions =
    props.options && searchTerm ? props.options.filter((x) => x.text.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1) : props.options;

  const searchOnChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setActiveOptionIndex(0);
  };
  const searchOnKeyDown = (e) => {
    if (filteredOptions && filteredOptions.length > 0) {
      if (e.keyCode === 13) {
        selectValue(filteredOptions[activeOptionIndex]);
        e.preventDefault();
      } else if (e.keyCode === 38) {
        setActiveOptionIndex((i) => Math.max(i - 1, 0));
        e.preventDefault();
      } else if (e.keyCode === 40) {
        setActiveOptionIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
        e.preventDefault();
      }
    }
  };

  const clearSelection = (e) => {
    props.onChange('');
    e.stopPropagation();
  };

  let trigger = props.trigger;
  if (props.selection) {
    let text = props.value;
    if (props.options) {
      const matchingOption = props.options.find((x) => x.value === props.value);
      if (matchingOption) {
        text = matchingOption.text;
      }
    }

    trigger = (
      <div className={`popup-dropdown-trigger selection ${props.clearable ? `clearable` : null}`}>
        {!searchTerm && !popupOpened ? <span className="text">{text}</span> : null}
        {props.placeholder && !searchTerm && !props.value ? <span className="placeholder">{props.placeholder}</span> : null}
        {props.search ? (
          <input
            className={`search ${props.clearable ? `clearable` : null}`}
            value={searchTerm || ''}
            onKeyDown={searchOnKeyDown}
            onChange={searchOnChange}
            autoComplete="stop-autocomplete"
            ref={searchInput}
          />
        ) : null}
        {props.value && props.clearable ? <Icon name="close" className="clearable-icon" onClick={clearSelection} /> : null}
        <i className="ui icon angle down" />
      </div>
    );
  }

  let content = props.content || props.children;
  if (filteredOptions) {
    content = (
      <PopupDropdown.Menu className="options">
        {filteredOptions.map((option, i) => (
          <PopupDropdown.Item key={option.value} onClick={() => selectValue(option)} active={i === activeOptionIndex} selected={option.value === props.value}>
            {option.text}
          </PopupDropdown.Item>
        ))}
        {filteredOptions.length === 0 ? <div className="no-results">No results found.</div> : null}
      </PopupDropdown.Menu>
    );
  }

  const contentWrapper = <Provider value={{ closePopup }}>{content}</Provider>;
  return (
    <CloseablePopup
      trigger={trigger}
      content={contentWrapper}
      on="click"
      basic={props.basic ? props.basic : false}
      position={(props.position as any) ?? 'bottom right'}
      // per: https://popper.js.org/docs/v2/modifiers/prevent-overflow/
      popperModifiers={[{ name: 'preventOverflow', options: { escapeWithReference: true } }]}
      offset={props.offset as any}
      className={`popup-dropdown${props.className ? ` ${props.className}` : ''}`}
      ref={popup}
      onClose={popupOnClose}
      onOpen={popupOnOpen}
    />
  );
};

PopupDropdown.Menu = (props: PopupDropdownMenuProps) => {
  return (
    <Menu vertical fluid className={props.className}>
      {props.children}
    </Menu>
  );
};

PopupDropdown.Header = ({ children }) => {
  return <div className="header">{children}</div>;
};

PopupDropdown.Item = (props: PopupDropdownItemProps) => {
  return (
    <Consumer>
      {(context) => (
        <Menu.Item
          onClick={() => {
            context.closePopup();
            if (props.onClick) {
              props.onClick();
            }
          }}
          active={props.active}
          className={props.selected ? 'selected' : ''}
        >
          {props.icon && <Icon name={props.icon as SemanticICONS} />}
          {props.text}
          {props.children}
        </Menu.Item>
      )}
    </Consumer>
  );
};

export default PopupDropdown;
